import { Base } from '../../abstract/base.model';
import { LoginPlatform } from '../../shared/utils/login-platform.utils';
import { isRecord } from '../utils/is-record';
import { nullableBoolean, nullableNumber, nullableString } from '../utils/wire-value-utils';
import { MembershipPlan } from './membership-plan.model';
import { TwoFactorMethod } from './two-factor-method.model';

/**
 * A user (profile) as `/admin/users` returns it (hub `admin-users-controller.ts`), built to the agreed
 * `ADMIN_USER_LIST_READABLE` / `ADMIN_USER_READABLE` lists. The list sends a subset; detail-only fields stay
 * `undefined` on list rows.
 *
 * Read-modify-write rule: writable fields the hub didn't send stay `undefined` (an explicit `null` stays
 * `null`). The hub never returns credentials, 2FA secrets, FCM tokens or the Firebase uid.
 */
export class User extends Base {
  // Writable via PUT /admin/users/:id (the hub's ADMIN_USER_WRITABLE)
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  requiresPasswordReset?: boolean | null;

  // Read-only
  emailVerified?: boolean | null;
  /** Whether the user set up 2FA. Not enforced server-side yet (separate card). */
  is2faEnabled?: boolean | null;
  twoFactorMethod?: TwoFactorMethod | null;
  firstLoginLocation?: LoginPlatform | null;
  lastLoginLocation?: LoginPlatform | null;
  isLocked?: boolean | null;
  membershipPlan?: MembershipPlan | null;
  failedLoginAttempts?: number | null;
  /** ISO timestamp as sent by the hub. */
  lockedUntil?: string | null;
  /** ISO timestamp as sent by the hub. */
  lastLogin?: string | null;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.readWritableFields(source);
    this.readHubFields(source);
  }

  private readWritableFields(source: Record<string, unknown>): void {
    this.firstName = nullableString(source['firstName']);
    this.lastName = nullableString(source['lastName']);
    this.email = nullableString(source['email']);
    this.phone = nullableString(source['phone']);
    this.address = nullableString(source['address']);
    this.city = nullableString(source['city']);
    this.state = nullableString(source['state']);
    this.zipCode = nullableString(source['zipCode']);
    this.latitude = nullableNumber(source['latitude']);
    this.longitude = nullableNumber(source['longitude']);
    this.requiresPasswordReset = nullableBoolean(source['requiresPasswordReset']);
  }

  private readHubFields(source: Record<string, unknown>): void {
    this.emailVerified = nullableBoolean(source['emailVerified']);
    this.is2faEnabled = nullableBoolean(source['is2faEnabled']);
    this.twoFactorMethod = nullableString(source['twoFactorMethod']) as TwoFactorMethod | null | undefined;
    this.firstLoginLocation = nullableString(source['firstLoginLocation']) as LoginPlatform | null | undefined;
    this.lastLoginLocation = nullableString(source['lastLoginLocation']) as LoginPlatform | null | undefined;
    this.isLocked = nullableBoolean(source['isLocked']);
    this.membershipPlan = nullableString(source['membershipPlan']) as MembershipPlan | null | undefined;
    this.failedLoginAttempts = nullableNumber(source['failedLoginAttempts']);
    this.lockedUntil = nullableString(source['lockedUntil']);
    this.lastLogin = nullableString(source['lastLogin']);
  }
}
